import React, { useState, useRef } from 'react';
import config from '../config/config';
import { Camera, Upload, Trash2, Loader, AlertTriangle, X } from 'lucide-react';
import apiService from '../services/api';

const ImageUpload = ({ userId, currentImageUrl, onImageUpdate, onImageDelete, userGender }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    // Validar tipo de archivo
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Solo se permiten archivos JPEG, PNG, GIF y WebP');
    }

    // Validar tamaño (5MB máximo)
    const maxSize = 5 * 1024 * 1024; // 5MB en bytes
    if (file.size > maxSize) {
      throw new Error('El archivo debe ser menor a 5MB');
    }
  };

  // Validar contenido inapropiado usando Cloudinary Moderation API
  const validateContent = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const imageBase64 = e.target.result;
          
          // Llamar al endpoint del backend que usa Cloudinary Moderation
          const response = await fetch(`${config.API_BASE_URL}/moderation/check-image`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
            },
            body: JSON.stringify({
              image: imageBase64
            })
          });

          const result = await response.json();
          
          if (!response.ok) {
            throw new Error(result.error || 'Error al validar la imagen');
          }

          // Verificar si la imagen fue aprobada
          if (result.unsafe) {
            reject(new Error('La imagen contiene contenido inapropiado y no puede ser subida. Por favor, usa una foto apropiada.'));
          } else {
            resolve();
          }
        } catch (err) {
          // Si hay error en la validación, permitir que continúe
          resolve();
        }
      };

      reader.onerror = () => {
        reject(new Error('Error al leer el archivo'));
      };

      reader.readAsDataURL(file);
    });
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleUpload(file);
    }
  };

  const handleUpload = async (file) => {
    try {
      setIsUploading(true);
      setIsValidating(true);
      setError('');

      // Validar archivo
      validateFile(file);

      // Validar contenido inapropiado
      await validateContent(file);

      setIsValidating(false);

      // Subir imagen
      const response = await apiService.uploadAvatar(userId, file);
      
      // Notificar al componente padre
      if (onImageUpdate) {
        // El backend devuelve 'profile_picture' en la respuesta
        const newImageUrl = response.profile_picture || response.profilePicture || response.imageUrl;
        onImageUpdate(newImageUrl);
      }

    } catch (err) {
      setError(err.message || 'Error al subir la imagen');
    } finally {
      setIsUploading(false);
      setIsValidating(false);
      // Limpiar el input file
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    setShowDeleteModal(false);
    
    try {
      setIsUploading(true);
      setError('');

      await apiService.deleteAvatar(userId);
      
      // Notificar al componente padre
      if (onImageDelete) {
        onImageDelete();
      }

    } catch (err) {
      setError(err.message || 'Error al eliminar la imagen');
    } finally {
      setIsUploading(false);
    }
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="relative">
      {/* Input file oculto */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={handleFileSelect}
        className="hidden"
        disabled={isUploading}
      />

      {/* Imagen actual */}
      <div className="relative inline-block">
        <img
          src={currentImageUrl || config.getDefaultAvatar(userGender || 'Hombre')}
          alt="Foto de perfil"
          className="w-48 h-48 rounded-full object-cover mx-auto border-4 border-white shadow-lg"
          onError={(e) => {
            e.target.src = config.getDefaultAvatar(userGender || 'Hombre');
          }}
        />
        
        {/* Overlay de carga */}
        {isUploading && (
          <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
            <div className="text-white text-center">
              <Loader className="h-6 w-6 animate-spin mx-auto mb-2" />
              <span className="text-sm">
                {isValidating ? 'Validando contenido...' : 'Subiendo...'}
              </span>
            </div>
          </div>
        )}

        {/* Botones de acción */}
        <div className="absolute bottom-0 right-0 flex space-x-2">
          <button
            onClick={triggerFileInput}
            disabled={isUploading}
            className="bg-primary text-white p-2 rounded-full hover:bg-primary-hover transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Cambiar foto"
          >
            {isUploading ? (
              <Loader className="h-4 w-4 animate-spin" />
            ) : (
              <Camera className="h-4 w-4" />
            )}
          </button>
          
          {currentImageUrl && (
            <button
              onClick={handleDeleteClick}
              disabled={isUploading}
              className="bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Eliminar foto"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Botón alternativo para subir */}
      {!currentImageUrl && (
        <div className="mt-4 text-center">
          <button
            onClick={triggerFileInput}
            disabled={isUploading}
            className="btn-outline flex items-center space-x-2 mx-auto"
          >
            <Upload className="h-4 w-4" />
            <span>Subir foto de perfil</span>
          </button>
        </div>
      )}

      {/* Mensaje de error */}
      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Información sobre restricciones */}
      <div className="mt-4 text-xs text-gray-500 text-center">
        <p>Formatos permitidos: JPEG, PNG, GIF, WebP</p>
        <p>Tamaño máximo: 5MB</p>
        <p>Se redimensionará automáticamente a 500x500px</p>
        <div className="mt-2 flex items-center justify-center space-x-1 text-orange-600">
          <AlertTriangle className="h-3 w-3" />
          <p className="font-semibold">Todas las imágenes son verificadas automáticamente</p>
        </div>
      </div>

      {/* Modal de confirmación para eliminar */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full transform transition-all duration-300 scale-100">
            {/* Header del modal */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="bg-red-100 p-2 rounded-full">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Eliminar foto de perfil</h3>
              </div>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="text-gray-400 hover:text-gray-600 hover:scale-110 transition-all duration-200"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Contenido del modal */}
            <div className="p-6">
              <p className="text-gray-600 mb-6">
                ¿Estás seguro de que quieres eliminar tu foto de perfil? 
                Esta acción no se puede deshacer y tu perfil mostrará el avatar por defecto.
              </p>
              
              {/* Botones de acción */}
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 hover:scale-105 active:scale-95 transition-all duration-200"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isUploading}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-md hover:shadow-lg flex items-center justify-center space-x-2 ${
                    isUploading 
                      ? 'bg-red-400 text-white cursor-not-allowed' 
                      : 'bg-red-600 text-white hover:bg-red-700 hover:scale-105 active:scale-95'
                  }`}
                >
                  {isUploading ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin" />
                      <span>Eliminando...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4" />
                      <span>Sí, eliminar</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
